import React, { useState } from 'react';
import './WordCard.css';
import casualIcon from '../assets/casual.svg';
import businessIcon from '../assets/business.svg';
import academicIcon from '../assets/academic.svg';
import avatarLexi from '../assets/avatar-lexi.svg';
import avatarSam from '../assets/avatar-sam.svg';
import avatarOwlbert from '../assets/avatar-owlbert.svg';



const personaAvatars = {
  teacher_lexi: avatarLexi,
  slang_sam: avatarSam,
  owlbert: avatarOwlbert,
};


const WordCard = ({
  word,
  pronunciation,
  pronunciationAudio,
  partOfSpeech = [],
  definition,
  example,
  personaExplanation = {},
  examples = {},
  similarWords = [],
  meanings = [],
  mnemonics = [],
  wordListTags = [] // ✅ new prop!,
}) => {
  const [activeTab, setActiveTab] = useState('get-it-fast');
  const personaKeys = Object.keys(personaExplanation);
  const [activeExplanation, setActiveExplanation] = useState(personaKeys[0] || '');
  

  // Helper function to filter school level tags based on priority
  const filterSchoolLevelTags = (tags) => {
    const normalizedTags = tags.map(tag => tag.toLowerCase());
    
    // If elementary school is present, only show that
    if (normalizedTags.includes('elementary school')) {
      return tags.filter(tag => tag.toLowerCase() === 'elementary school');
    }
    
    // If middle school is present (and no elementary), only show that
    if (normalizedTags.includes('middle school')) {
      return tags.filter(tag => tag.toLowerCase() === 'middle school');
    }
    
    // If only high school is present, show that
    if (normalizedTags.includes('high school')) {
      return tags.filter(tag => tag.toLowerCase() === 'high school');
    }
    
    // For non-school tags or other cases, return as is
    return tags.filter(tag => 
      !['elementary school', 'middle school', 'high school'].includes(tag.toLowerCase()) ||
      tag.toLowerCase() === normalizedTags[0]
    );
  };

  const renderExamples = () => {
    if (!examples || Object.keys(examples).length === 0) {
      return <p className="no-examples">No example sentences available.</p>;
    }
  
    const iconMap = {
      casual: casualIcon,
      business: businessIcon,
      academic: academicIcon
    };

    const highlightWord = (text) => {
      // Create a case-insensitive regular expression to match the word
      const regex = new RegExp(`\\b${word}\\b`, 'gi');
      // Split the text into parts and map them to either text or bold elements
      const parts = text.split(regex);
      const matches = text.match(regex);
      
      if (!matches) return text;

      return parts.map((part, index) => (
        <>
          {part}
          {index < parts.length - 1 && <strong>{matches[index]}</strong>}
        </>
      ));
    };
  
    return (
      <div className="examples-content">
        <span className="see-in-action-tag">See it in action</span>
        <div className="examples-list">
          {['casual', 'business', 'academic'].map(type => (
            examples[type] && examples[type].length > 0 && (
              <div key={type} className={`example-item ${type} ${examples[type][0].isPlaceholder ? 'placeholder' : ''}`}>
                <div className="example-icon">
                  <img src={iconMap[type]} alt={type} />
                </div>
                <p className="example-text">{highlightWord(examples[type][0].text)}</p>
              </div>
            )
          ))}
        </div>
      </div>
    );
  };  

  const renderSimilarWords = () => {
    if (!similarWords || similarWords.length === 0) {
      return (
        <div className="similar-words-content">
          <span className="similar-words-tag">Similar words</span>
          <p className="no-similar-words">No similar words available.</p>
        </div>
      );
    }

    return (
      <div className="similar-words-content">
        <span className="similar-words-tag">Similar words</span>
        <div className="word-groups">
          {similarWords.map((group, i) => (
            <div key={i} className="word-group">
              {group.map((w, j) => (
                <a key={j} href={`/word/${w}`} className="similar-word">
                  {w}
                </a>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  };
  //placeholders for meanings and make it stick
  const renderMeanings = () => (
    <div className="meanings-content">
      <span className="all-meanings-tag">All meanings</span>
      {(!meanings || meanings.length === 0) ? (
        <p className="no-meanings">No additional meanings available.</p>
      ) : (
        <div className="meanings-list">
          {meanings.map((meaning) => (
            <div key={`${meaning.word}-${meaning.meaning_number}`} className="meaning-item">
              <div className="meaning-number">{meaning.meaning_number}</div>
              <div className="meaning-content">
                <p className="meaning-definition">{meaning.meaning}</p>
                <p className="meaning-example italic">{meaning.example}</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
  
  const renderMakeItStick = () => (
    <div className="make-it-stick-content">
      <span className="make-it-stick-tag">Make it stick</span>
      <div className="mnemonic-content">
        {(!mnemonics || mnemonics.length === 0) ? (
          <p className="no-mnemonics">No memory hacks available yet.</p>
        ) : (
          <div className="mnemonics-list">
            {mnemonics.map((mnemonic, index) => (
              <div key={`${word}-mnemonic-${index}`} className="mnemonic-item">
                <p className="mnemonic-main">{mnemonic.content}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );

  const renderGetItFast = () => (
    <div className="get-it-fast-content">
      <span className="get-it-fast-tag">Get it fast</span>
      {wordListTags.length > 0 && (
        <div className="right-tags">
          {filterSchoolLevelTags(wordListTags).map((tag, index) => (
            <span key={index} className={`vocab-tag ${tag.toLowerCase().replace(/\s/g, '-').replace(/[^\w-]/g, '-')}`}>
              {tag}
            </span>
          ))}
        </div>
      )}
      <div className="definition-section">
        <p className="word-definition">{definition}</p>
        <p className="word-example italic">{example}</p>
      </div>

      {activeExplanation && personaExplanation[activeExplanation] && (
        <div className="explanation-section">
          <div className="explanation-header">
            <div className="avatar-container">
              <img
                src={personaAvatars[activeExplanation]}
                alt={activeExplanation}
                className="avatar-image"
              />
              {personaExplanation[activeExplanation].audio_url && (
                <button className="play-button" onClick={() => new Audio(personaExplanation[activeExplanation].audio_url).play()}>
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M8 5v14l11-7z"/>
                  </svg>
                </button>
              )}
            </div>
            <p className="explanation-text">{personaExplanation[activeExplanation].explanation}</p>
          </div>

          <div className="explanation-footer">
            <div className="avatar-group">
              {personaKeys
                .filter(p => p !== activeExplanation)
                .map(p => (
                  <button
                    key={p}
                    className="mini-avatar"
                    onClick={() => setActiveExplanation(p)}
                    aria-label={`Switch to explanation ${p}`}
                  >
                    <img src={personaAvatars[p]} alt={`Avatar ${p}`} />
                  </button>
                ))}
            </div>
            <p className="tap-discover-text">See how we explain it - Tap to discover!</p>
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="word-card">
      <div className="header-bar">
        <div className="left-cluster">
          <h2 className="word-title">{word}</h2>
          {(pronunciation || pronunciationAudio) && (
            <div className="pronunciation-section">
              <span className="pronunciation-text">{pronunciation}</span>
              {pronunciationAudio && (
                <button className="pronunciation-audio-button" onClick={() => new Audio(pronunciationAudio).play()}>
                  <svg xmlns="http://www.w3.org/2000/svg" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M8 5v14l11-7z" />
                  </svg>
                </button>
              )}
            </div>
          )}
          <div className="word-tags">
            {partOfSpeech.map((pos, index) => (
              <span key={index} className={`pos-tag ${pos.toLowerCase()}`}>{pos}</span>
            ))}
          </div>
        </div>

        {wordListTags.length > 0 && (
          <div className="right-tags">
            {filterSchoolLevelTags(wordListTags).map((tag, index) => (
              <span key={index} className={`vocab-tag ${tag.toLowerCase().replace(/\s/g, '-').replace(/[^\w-]/g, '-')}`}>
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>

      <div className="word-content">
        {activeTab === 'examples' && renderExamples()}
        {activeTab === 'similar-words' && renderSimilarWords()}
        {activeTab === 'get-it-fast' && renderGetItFast()}
        {activeTab === 'all-meanings' && renderMeanings()}
        {activeTab === 'make-it-stick' && renderMakeItStick()}
      </div>
      

      <div className="word-tabs">
        <button className={`tab ${activeTab === 'get-it-fast' ? 'active' : ''}`} onClick={() => setActiveTab('get-it-fast')}>
          Get it fast
        </button>
        <button className={`tab ${activeTab === 'examples' ? 'active' : ''}`} onClick={() => setActiveTab('examples')}>
          See it in action
        </button>
        <button className={`tab ${activeTab === 'similar-words' ? 'active' : ''}`} onClick={() => setActiveTab('similar-words')}>
          Similar words
        </button>
        <button className={`tab ${activeTab === 'all-meanings' ? 'active' : ''}`} onClick={() => setActiveTab('all-meanings')}>
          All meanings
        </button>
        <button className={`tab ${activeTab === 'make-it-stick' ? 'active' : ''}`} onClick={() => setActiveTab('make-it-stick')}>
          Make it stick
        </button>
      </div>
    </div>
  );
};

export default WordCard;
